import { useEffect, useState } from "react";
import { handleMessageError, handleMessageSuccess } from "../../components/toast";
import { alternarModal, filtrosIniciais, listarAplicados, paraConsulta } from "./filtros";
import { ACOES, descrever, simularAcao } from "./status";

const POR_PAGINA = 15;

export function mensagemDeErro(erro) {
	const mensagem = erro?.message;
	return typeof mensagem === "string" ? mensagem : JSON.stringify(mensagem);
}

export function useConsultaFiscal(listar, identFiltro) {
	const [filtros, setFiltros] = useState(filtrosIniciais);
	const [filtrosAtivos, setFiltrosAtivos] = useState(null);
	const [itens, setItens] = useState([]);
	const [carregando, setCarregando] = useState(false);

	async function buscar(novosFiltros) {
		setCarregando(true);
		try {
			setFiltros(novosFiltros);
			setItens(await listar(paraConsulta(novosFiltros)));
			setFiltrosAtivos(novosFiltros);
		} catch (erro) {
			setItens([]);
			handleMessageError(mensagemDeErro(erro));
		} finally {
			setCarregando(false);
		}
	}

	async function filtrar(novosFiltros) {
		await buscar(novosFiltros);
		alternarModal(identFiltro);
	}

	return {
		identFiltro,
		filtros,
		filtrosAplicados: filtrosAtivos ? listarAplicados(filtrosAtivos) : null,
		abrirFiltro: () => alternarModal(identFiltro),
		filtrar,
		recarregar: () => buscar(filtros),
		itens,
		carregando,
	};
}

export function usePaginacao(itens) {
	const [pagina, setPagina] = useState(1);

	useEffect(() => {
		setPagina(1);
	}, [itens]);

	return {
		pagina,
		setPagina,
		totalPaginas: Math.max(1, Math.ceil(itens.length / POR_PAGINA)),
		itensPagina: itens.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA),
	};
}

export function useAcoesFiscais(executores = {}, aoAlterar = () => {}) {
	const [painel, setPainel] = useState(null);
	const [numero, setNumero] = useState(null);
	const [pedido, setPedido] = useState(null);

	function executar(acao, item) {
		const { aba, painelNumero, justificativa } = ACOES[acao];
		const executor = executores[acao];

		if (aba) return setPainel({ item, aba });
		if (painelNumero) return setNumero(item);
		if (justificativa) return setPedido({ acao, item, simulado: !executor });
		if (executor) return executarDireto(acao, item, executor);

		simularAcao(acao, item);
	}

	async function executarDireto(acao, item, executor) {
		const { sucesso } = ACOES[acao];

		try {
			const retorno = await executor(item);
			if (sucesso) handleMessageSuccess(`${descrever(item)} ${sucesso}.`);
			return retorno;
		} catch (erro) {
			handleMessageError(mensagemDeErro(erro));
		} finally {
			if (sucesso) {
				setPainel(null);
				aoAlterar();
			}
		}
	}

	async function confirmar(justificativa) {
		const { acao, item } = pedido;
		const executor = executores[acao];

		if (!executor) {
			simularAcao(acao, item);
			return setPedido(null);
		}

		await executor(item, justificativa);
		handleMessageSuccess(`${descrever(item)} ${ACOES[acao].sucesso}.`);
		setPedido(null);
		setPainel(null);
		aoAlterar();
	}

	return {
		executar,
		painel,
		trocarAba: (aba) => setPainel((atual) => ({ ...atual, aba })),
		fecharPainel: () => setPainel(null),
		numero,
		fecharNumero: () => setNumero(null),
		pedido,
		confirmar,
		desistir: () => setPedido(null),
	};
}
